import { useState, useEffect, useRef } from "react";
import {
    AddCallerIdResponse,
    usePhoneNumberVerificationStatus,
    PHONE_NUMBERS_QUERY_KEY,
} from "@/state/hooks/usePhoneCall";
import { formatPhoneForDisplay } from "@/common/utils/phone";
import CallerIdVerificationContent, {
    type CallerIdVerificationStatus,
} from "@/features/phone-call-widget/CallerIdVerificationContent";
import { useQueryClient } from "@tanstack/react-query";

// EN-1051: was 20000, which is shorter than it takes to answer a phone and read back a code — the
// widget offered "Retry" mid-verification while still displaying the code and no explanation.
// Matches CallerIdVerificationModal's value so both surfaces behave the same.
const VERIFICATION_TIMEOUT_MS = 60000;

interface CallerIdVerifyProps {
    verificationData: AddCallerIdResponse;
    onCancel: () => void;
    onVerifiedContinue: () => void;
    onRetry: () => void;
}

/**
 * CallerIdVerify component for verifying a caller ID
 * Displays the verification code and shows success state when verified
 */
const CallerIdVerify = ({ verificationData, onCancel, onVerifiedContinue, onRetry }: CallerIdVerifyProps) => {
    const [isVerified, setIsVerified] = useState(false);
    const [isTimedOut, setIsTimedOut] = useState(false);
    const { data: verificationStatus } = usePhoneNumberVerificationStatus(verificationData.id);
    const queryClient = useQueryClient();
    const formattedPhoneNumber = formatPhoneForDisplay(verificationData.phoneNumber || "");
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Start the timeout timer
        timeoutRef.current = setTimeout(() => {
            setIsTimedOut(true);
        }, VERIFICATION_TIMEOUT_MS);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // EN-1051 (final review, C2): success is latched, failure is NOT.
    //
    // This component used to latch both. A latch on "failed" is one-way, so once the query returned
    // a terminal "failed" — including a stale one left in the cache by a previous attempt on the
    // same reused row id — the component could never leave that state, and the failed branch renders
    // no verification code. That was the headline EN-1051 symptom on the dialer widget: verify, let
    // it fail, Cancel, re-enter the same number, submit — Twilio is calling but the widget says
    // "Not Verified" with no code, permanently. Deriving "failed" from the query (the way
    // CallerIdVerificationModal already does) is what makes a later "pending" recoverable.
    //
    // Success stays latched deliberately: `setQueryData` from realtime and this hook's own poll both
    // write the same key and are not ordered against each other (see the hazard note on
    // usePhoneNumberVerificationStatus), so a poll that resolves after the success push can carry a
    // stale "pending". Latching means a verified number cannot flap back and yank the Continue
    // button out from under the vet.
    useEffect(() => {
        if (verificationStatus?.status === "success") {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            queryClient.invalidateQueries({ queryKey: PHONE_NUMBERS_QUERY_KEY });
            setIsVerified(true);
        }
    }, [verificationStatus?.status, queryClient]);

    // Not latched — recomputed on every status change.
    const isFailed = !isVerified && verificationStatus?.status === "failed";

    const handleContinue = () => {
        onVerifiedContinue();
    };

    return (
        <div className="w-full h-full flex flex-col bg-shadow-950 rounded-lg shadow">
            {/* Header - shadow-850 #232C33 to match Dialer */}
            <div className="px-6 py-5 flex flex-col gap-1 bg-shadow-850">
                <h3 className="text-white text-lg font-semibold">Verify Your Phone Number</h3>
                <p className="text-gray-400 text-sm">
                    You'll receive a call at {formattedPhoneNumber}. Enter the code below.
                </p>
            </div>

            {/* Content */}
            <div className="flex-1 bg-shadow-950 px-6 py-5 flex flex-col items-center justify-center gap-6">
                <CallerIdVerificationContent
                    variant="dark"
                    status={((): CallerIdVerificationStatus => {
                        if (isVerified) return "verified";
                        if (isFailed) return "failed";
                        return "pending";
                    })()}
                    verificationCode={verificationData.verificationCode ?? ""}
                    copyButtonId="copy-verification-code"
                />
            </div>

            {/* Footer buttons */}
            <div className="px-6 py-5 bg-shadow-800 flex gap-3 rounded-b-lg">
                {isVerified ? (
                    <button onClick={handleContinue} className="primary-button flex-1">
                        Continue
                    </button>
                ) : (
                    <>
                        <button onClick={onCancel} className="secondary-button flex-1 !text-max-800">
                            Cancel
                        </button>
                        {(isFailed || isTimedOut) && (
                            <button onClick={onRetry} className="primary-button flex-1">
                                Retry
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CallerIdVerify;
